import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ArrowLeft, Calendar, Users, Trophy, TrendingUp } from 'lucide-react'

interface HistoryPageProps {
  onBack: () => void
  onApplyClick: () => void
}

const HistoryPage: React.FC<HistoryPageProps> = ({ onBack, onApplyClick }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  
  const [hoveredEvent, setHoveredEvent] = useState<number | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Block scroll when modal is open
  useEffect(() => {
    if (hoveredEvent !== null || selectedImage !== null) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [hoveredEvent, selectedImage])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHoveredEvent(null)
        setSelectedImage(null)
      }
    }

    if (hoveredEvent !== null || selectedImage !== null) {
      document.addEventListener('keydown', handleEsc)
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  }, [hoveredEvent, selectedImage])

  const timelineEvents = [
    {
      year: '13 апреля 2024',
      title: 'Начало семьи Hugo',
      description: 'Richard Hugo основал семью вместе с Quincy и Logan. Начало великой истории Hugo Family.',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      tooltip: {
        title: 'Начало пути',
        content: `В этот день в штат Washington прилетели Richard и Logan. Двое парней с большими амбициями, желанием оставить след и громко заявить: Hugo здесь.
Первый день прошёл, как у всех - документы, поиски работы, фарм… но уже вечером у нас был Benefactor Runner с номером 1LAB (теперь как трофей в офисе) и Vapid Contender.
На следующей неделе подтянулись и Quincy, и Victor. Денег хватало, ночи проходили в загрузке-разгрузке, а по утрам — поставки, первые шаги в логистике.
24 апреля — первая недвижимость. 4 ГМ в Палето-Бэй. Это был наш первый "угол", куда мы вложили не только деньги, но и веру в будущее.
На следующий день — открытие семьи. Момент, когда Hugo стала не просто фамилией, а объединением.
27 апреля — к нам пришли Zames и Pinky. С первого дня — полная самоотдача. Потом — Lanirr, Tony. Мы ещё не знали, что это станет ядром, которое понесёт нас вперёд.
`,
        images: [
          '/content/Story/1/IMG_1446.png',
          '/content/Story/1/e9971ce7-2d34-4948-bd71-156249d2695d.png',
          '/content/Story/1/20250421105800_1.jpg',
          '/content/Story/1/1b5f855d-9988-42fa-89d4-e0bf94dd129f.png',
          '/content/Story/1/b5df79ed-38e3-4b1f-b914-2973a6640420.png'
        ]
      }
    },
    {
      year: '5 мая 2024',
      title: 'Первые достижения',
      description: 'Семья начала набирать обороты.',
      icon: Trophy,
      color: 'from-yellow-500 to-orange-500',
      tooltip: {
        title: 'Дом, семья и свадьба',
        content: `5 мая — мы справляем новоселье в новом 10 ГМ доме, и… у нас появился вертолёт, который регулярно “тонул” в бассейне. На фоне — наш первый Pfister Teycan, выигранный в лудке.
Люди приходили каждый день. Кто-то на день, кто-то навсегда. Мы не всегда запоминали имена, но чётко чувствовали — идём в правильном направлении.
Жизнь была насыщенной: на скринах — кто-то курит грин, кто-то танцует у бассейна. Вайб был таким, каким должен быть в начале легенды.
Но рядом кипела жизнь — Вайнвуд оказался "весёлым" районом. Стрельба через забор, крайм-семьи по соседству. Часто нас цепляло, иногда — мы отвечали.
18 мая — важный день. Свадьба @Nami и @Polinysss. Кортеж из геликов, похищение невесты, выкуп на Чиллиаде, Марсель едет туда на велике от аэропорта… Это была не просто свадьба — это был праздник семьи. Позже Руслан сделал Полине предложение уже в реальной жизни. ❤️`,
        images: [
          '/content/Story/2/ima3333ge.png',
          '/content/Story/2/19d05788-8e1c-4fdb-80f1-0ef4d339fc17.png',
          '/content/Story/2/f030f85a-1312-40fe-8e88-fa152ef1b996.png',
          '/content/Story/2/image.png'
        ]
      }
    },
    {
      year: '26 мая – август 2024',
      title: 'Расширение семьи',
      description: 'Госка, стабильность и NSB.',
      icon: Users,
      color: 'from-green-500 to-emerald-500',
      tooltip: {
        title: 'Госка, стабильность и NSB',
        content: `26 мая - мы устаём от фарма и пробуем себя в FIB. Вход был волнительным - экзамены, проверки… Но нас приняли. Отдел ATF стал нашим первым пристанищем.
Там мы впервые пересеклись с Deckard Indzagi и который уже тогда был строг и жаден на баллы. Нас пугали имена вроде Декарда, Декстера, Шкета.
Но мы не косячили. Мы шли от ранга к рангу. Логан был первым, кто получил повышение - мы даже соревновались, кто быстрее.
Июнь–июль - стабильная госслужба. Но накопилась усталость. Старший состав начал уходить в отпуска. Кто-то вернулся, как Квинси и Логан. Кто-то пока нет. Мы скучаем.
В июле переехали в новый, шикарный дом, в котором прожили до конца года.
26 августа - возвращение в госку. Мы думали просто начать с нуля, а получили сообщение: нам предлагают отдел NSB.`,
        images: [
          '/content/Story/3/Grand_Theft_Auto_V_Screenshot_2024.07.29_-_00.04.37.96.png',
          '/content/Story/3/2025-04-23_13.24.11.jpg',
          '/content/Story/3/2025-04-23_13.23.40.jpg'
        ]
      }
    },
    {
      year: 'Август 2024 – январь 2025',
      title: 'Первая лидерка',
      description: 'Hugo в роли лидеров.',
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      tooltip: {
        title: 'Второй срок и переход в FIB',
        content: `Мы не планировали брать должности, не просили блат. Но NSB достался нам. Первый хэд… ушёл ПСЖ через 2 дня. Пришлось брать всё на себя.
Работали как звери - перевыполняли планы, не получали ни одного выговора, держали трассу как бетон.
Сентябрь - Декард ушёл с лидерки. Октябрь - снова встал на пост.
И NSB под Hugo продержался 6 месяцев.
В это время мы выросли до 40+ активных участников с фамилией Hugo. К нам пришли Federick, Larry, Valerie - все трое внесли огромный вклад в развитие.
14 января 2025 - первый лидерский срок семьи. Сообщение от Леши Декстера: “я лидер”. Мы офигели. Всё шло не по плану, лагало, не заходило, но - получилось.
Старт был нервный, но мы не облажались. Два срока, без выговоров, с бешеным активом. Вернулся даже Logan - это была настоящая победа.`,
        images: [
          
          '/content/Story/4/image.png',
          '/content/Story/4/shot.png'
        ]
      }
    },
    {
      year: 'Февраль – апрель 2025',
      title: 'Перезагрузка',
      description: 'Современный этап развития семьи Hugo Family.',
      icon: Calendar,
      color: 'from-indigo-500 to-purple-500',
      tooltip: {
        title: 'Наши дни',
        content: `После второго срока — снова FIB, всей семьёй.
Потом — неожиданный виток. Мы пробуем себя в крайме. Убираем временно правило о госограничении, идём в Bloods.
Казалось бы, Hugo — это про госку, но реакция семьи удивила: никто не возмутился. Наоборот — восприняли как возможность. Это и есть наша сила: гибкость.
19 апреля — приход большого состава. Мы наполняем войсчаты, оживаем. Новички приживаются, возвращается атмосфера.
23 апреля — Deckard получает лидерку EMS.
24 апреля — Hugo проводит первую хирургическую “операцию” по увеличению члена Tony. Всё по канонам, с юмором и заботой. 😄`,
        images: ['/content/Story/4/Grand_Theft_Auto_V_Screenshot_2025.png',
          ]
      }
    }
  ]

  return (
    <div className="min-h-screen bg-dark-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img 
          src="/content/story_bg.png" 
          alt="History background" 
          className="w-full h-full object-cover opacity-100 blur-"
        />
        <div className="absolute inset-0 bg-dark-900/90"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-dark-800/80 backdrop-blur-md border-b border-dark-700 sticky top-0 z-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors w-10 sm:w-auto"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Назад</span>
              </button>
              
              <h1 className="text-2xl font-display font-bold text-white">
                История <span className="gradient-text">Hugo Family</span>
              </h1>
              
              <div className="w-20"></div> {/* Spacer for centering */}
            </div>
          </div>
        </motion.header>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center px-4 py-2 bg-primary-600/20 border border-primary-500/30 rounded-full text-primary-300 text-sm font-medium mb-6"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Наш путь
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-4xl md:text-5xl font-display font-bold text-white mb-6"
            >
              Полная история становления
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              От основания до сегодняшнего дня - весь путь нашей семьи, 
              наполненный событиями, достижениями и крепкими узами дружбы.
            </motion.p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <motion.div 
              initial={{ scaleY: 0 }}
              animate={inView ? { scaleY: 1 } : {}}
              transition={{ delay: 0.3, duration: 1.5, ease: "easeOut" }}
              className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-primary-600 origin-top"
            ></motion.div>
            
            {/* Timeline events */}
            <div className="space-y-16">
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.2, duration: 0.8 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-col md:space-x-8 space-y-4`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full border-4 border-dark-800 z-10"></div>
                  
                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-20' : 'md:pl-14'} text-center md:text-left relative`}>
                    <div 
                      className={`bg-dark-800/80 backdrop-blur-sm border rounded-xl p-6 transition-all duration-300 ${
                        event.tooltip 
                          ? 'border-primary-500/50 hover:border-primary-400/70' 
                          : 'border-dark-700 hover:border-dark-600'
                      }`}
                    >
                      <div className="flex items-center justify-center md:justify-start mb-4">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${event.color} flex items-center justify-center mr-4`}>
                          <event.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="text-primary-400 font-semibold text-sm">{event.year}</div>
                          <h3 className="text-xl font-display font-bold text-white flex items-center">
                            {event.title}
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-300 leading-relaxed">{event.description}</p>
                      
                      {event.tooltip && (
                        <button
                          onClick={() => setHoveredEvent(hoveredEvent === index ? null : index)}
                          className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600/20 border border-primary-500/30 rounded-lg text-primary-300 text-sm font-medium hover:bg-primary-600/30 hover:border-primary-400/50 transition-all duration-300 w-full sm:w-auto"
                        >
                          Подробнее
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Future section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="text-center mt-20"
          >
            <div className="p-8">
              <h3 className="text-2xl font-display font-bold text-white mb-4">
                Будущее Hugo Family
              </h3>
              <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Мы продолжаем развиваться и становиться еще сильнее. 
                Стань частью HUGO и помоги нам написать новые главы нашей истории.
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto max-w-md"
                onClick={onApplyClick}
              >
                Подать заявку
              </motion.button>
            </div>
          </motion.div>
        </div>
        
        {/* Modal for tooltip - rendered at the top level */}
        {hoveredEvent !== null && timelineEvents[hoveredEvent]?.tooltip && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999]"
              onClick={() => setHoveredEvent(null)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 flex items-center justify-center z-[999999] p-4"
            >
              <div className="backdrop-blur-xl rounded-xl p-8 shadow-2xl w-[95vw] h-[90vh] overflow-y-auto hide-scrollbar">
                <h4 className="text-2xl font-display font-bold text-white mb-6">{timelineEvents[hoveredEvent].tooltip.title}</h4>
                <div className="text-gray-300 text-lg leading-relaxed mb-8 whitespace-pre-line">
                  {timelineEvents[hoveredEvent].tooltip.content}
                </div>
                
                {/* Images */}
                {timelineEvents[hoveredEvent].tooltip.images && timelineEvents[hoveredEvent].tooltip.images.length > 0 && (
                  <div className="space-y-6">
                    <h5 className="text-primary-400 font-semibold text-lg">Фотографии:</h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {timelineEvents[hoveredEvent].tooltip.images.map((image, imgIndex) => (
                        <div 
                          key={imgIndex} 
                          className="relative group overflow-hidden rounded-lg border border-dark-600 hover:border-primary-500/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedImage(image)}
                        >
                          <img 
                            src={image} 
                            alt={`История ${hoveredEvent + 1} - фото ${imgIndex + 1}`}
                            className="w-full h-32 md:h-40 object-cover rounded-lg transition-all duration-300 group-hover:scale-105"
                            onError={(e) => {
                              console.error('Failed to load image:', image);
                              e.currentTarget.style.display = 'none';
                            }}
                            onLoad={() => {}}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium">
                              Кликните для просмотра
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Close button */}
                <button
                  onClick={() => setHoveredEvent(null)}
                  className="absolute top-4 right-4 p-2 hover:bg-dark-700 rounded-lg transition-colors w-12 h-12 flex items-center justify-center"
                >
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </motion.div>
          </>
        )}
        
        {/* Image Modal */}
        {selectedImage && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[999999]"
              onClick={() => setSelectedImage(null)}
            />
            
            {/* Image Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 flex items-center justify-center z-[999999] p-4"
              onClick={() => setSelectedImage(null)}
            >
              <div className="relative w-full h-full flex items-center justify-center p-4">
                <div 
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img 
                    src={selectedImage} 
                    alt="Full size image"
                    className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
                    style={{
                      maxWidth: 'calc(100vw - 2rem)',
                      maxHeight: 'calc(100vh - 2rem)',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      console.error('Failed to load image in modal:', selectedImage);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  
                  {/* Close button positioned on the image */}
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors z-10 w-12 h-12 flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryPage 